
import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
  Switch,
  Stack,
  TextField,
  Grid,
  Alert
} from '@mui/material';
import { Order } from "../../types/order";
import { Conference } from "../../types/conference";

interface RoomRequirementsProps {
  order: Order;
  setOrderProp: (field: string, value: any) => void;
  valid?: boolean;
}

export default function RoomRequirements({ order, setOrderProp, valid }: RoomRequirementsProps) {

  useEffect(() => {
    if (!order.selectedArrivalDate) {
      setOrderProp('selectedArrivalDate', order.conference_group.conference.defaultArrivalDate);
    }
    if (!order.selectedDepartureDate) {
      setOrderProp('selectedDepartureDate', order.conference_group.conference.defaultDepartureDate);
    }
  }, []);
  
  const handleTransportationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrderProp('transportation', event.target.value);
  };

  // Helper function to format date for input field
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const handleToggleChange = (field: 'requiresBusParking' | 'requiresTransitToVenue') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderProp(field, event.target.checked);
  };

  const handleRoomNumberChange = (field: 'oneBedRoomsNeeded' | 'twoBedRoomsNeeded') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (parseInt(value) < 0) return; // Prevent negative numbers
    setOrderProp(field, value);
  };

  const handleDateChange = (field: 'selectedArrivalDate' | 'selectedDepartureDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderProp(field, event.target.value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Transportation
          </Typography>
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={order.requiresBusParking || false}
                  onChange={handleToggleChange('requiresBusParking')}
                  name="requiresBusParking"
                />
              }
              label="I need bus parking at the hotel"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={order.requiresTransitToVenue || false}
                  onChange={handleToggleChange('requiresTransitToVenue')}
                  name="requiresTransitToVenue"
                />
              }
              label="I need transportation between the hotel and the conference"
            />
          </Stack>
          </Box>
          
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ my: 3 }}>
              Rooms
            </Typography>
            <Typography variant="body1" color={valid ? "text.secondary" : "error"} sx={{ my: 3 }}>
              At least one room is required. Please specify how many 1-bed and/or 2-bed rooms you need.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Number of 1-Bed Rooms"
                  type="number"
                  value={order.oneBedRoomsNeeded}
                  onChange={handleRoomNumberChange('oneBedRoomsNeeded')}
                  helperText="How many rooms with 1 bed do you need?"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Number of 2-Bed Rooms"
                  type="number"
                  value={order.twoBedRoomsNeeded}
                  onChange={handleRoomNumberChange('twoBedRoomsNeeded')}
                  helperText="How many rooms with 2 beds do you need?"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          
          <Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Check In Date"
                  type="date"
                  value={order.selectedArrivalDate || formatDateForInput(order.conference_group.conference.defaultArrivalDate)}
                  onChange={handleDateChange('selectedArrivalDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formatDateForInput(order.conference_group.conference.earliestArrivalDate),
                    max: formatDateForInput(order.conference_group.conference.defaultDepartureDate)
                  }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Check Out Date"
                  type="date"
                  value={order.selectedDepartureDate || formatDateForInput(order.conference_group.conference.defaultDepartureDate)}
                  onChange={handleDateChange('selectedDepartureDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formatDateForInput(order.conference_group.conference.defaultArrivalDate),
                    max: formatDateForInput(order.conference_group.conference.defaultDepartureDate)
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Validation Errors */}
          <Box sx={{ mt: 3 }}>
            {((order?.oneBedRoomsNeeded || 0) === 0 && (order?.twoBedRoomsNeeded || 0) === 0) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select at least one room (1-bed or 2-bed rooms).
              </Alert>
            )}
            {!order?.selectedArrivalDate && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select a check-in date.
              </Alert>
            )}
            {!order?.selectedDepartureDate && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select a check-out date.
              </Alert>
            )}
          </Box>
      </Stack>
    </Box>
  );
}